import { CheckCircle2, Circle, TrendingDown, TrendingUp } from 'lucide-react'
import { Link } from 'react-router-dom'

import { ReactNode, useState } from 'react'

import { cn } from '@/lib/utils'
import { resolveMediaUrl } from '@/lib/media/resolveUrl'
import { AssistantCard } from '@/types/assistant'

import { ImageActions } from './ImageActions'

// Replies are rendered from a small Markdown subset into React elements (never as HTML): **bold**, lists, headings
// as bold lines, and links to dashboard paths only.

const inline = (text: string, key: string): ReactNode[] => {
  const out: ReactNode[] = []
  const pattern = /\*\*([^*]+)\*\*|\[([^\]]+)\]\((\/[^)\s]*)\)/g
  let last = 0
  let match: RegExpExecArray | null
  let i = 0
  while ((match = pattern.exec(text))) {
    if (match.index > last) out.push(text.slice(last, match.index))
    if (match[1]) out.push(<strong key={`${key}-b${i}`} className="font-semibold">{match[1]}</strong>)
    else out.push(<Link key={`${key}-l${i}`} to={match[3]} className="text-link underline-offset-2 hover:underline">{match[2]}</Link>)
    last = match.index + match[0].length
    i += 1
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

export const AssistantMarkdown = ({ text }: { text: string }) => {
  const blocks: ReactNode[] = []
  const lines = text.replace(/\r/g, '').split('\n')
  let list: { ordered: boolean; items: string[] } | null = null

  const flush = () => {
    if (!list) return
    const Tag = list.ordered ? 'ol' : 'ul'
    blocks.push(
      <Tag key={`list${blocks.length}`} className={cn('space-y-1 pl-5', list.ordered ? 'list-decimal' : 'list-disc')}>
        {list.items.map((item, i) => <li key={i}>{inline(item, `li${blocks.length}-${i}`)}</li>)}
      </Tag>,
    )
    list = null
  }

  lines.forEach((raw, index) => {
    const line = raw.trimEnd()
    const bullet = line.match(/^\s*[-*•]\s+(.*)$/)
    const numbered = line.match(/^\s*\d+[.)]\s+(.*)$/)
    if (bullet || numbered) {
      const ordered = Boolean(numbered)
      if (list && list.ordered !== ordered) flush()
      if (!list) list = { ordered, items: [] }
      list.items.push((bullet ?? numbered)![1])
      return
    }
    flush()
    if (!line.trim() || /^-{3,}$/.test(line.trim())) return
    const heading = line.match(/^#{1,6}\s+(.*)$/)
    blocks.push(
      <p key={`p${index}`} className={heading ? 'font-semibold' : undefined}>
        {inline(heading ? heading[1] : line, `p${index}`)}
      </p>,
    )
  })
  flush()

  return <div className="space-y-2 break-words">{blocks}</div>
}

const formatValue = (value: string | number) =>
  typeof value === 'number' ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : value

export const AssistantCardView = ({ card, onRoute }: { card: AssistantCard; onRoute?: (routeId: string) => void }) => {
  if (card.kind === 'kpis') {
    return (
      <div className="rounded-lg border border-border-subtle bg-card p-3">
        <p className="text-xs font-medium text-muted-foreground">{card.title}</p>
        <div className="mt-2 grid grid-cols-2 gap-3">
          {card.items.map((item) => (
            <div key={item.label}>
              <p className="text-xs text-muted-foreground">{item.label}</p>
              <p className="text-heading-md">{formatValue(item.value)}</p>
              {typeof item.change === 'number' && (
                <p className={cn('flex items-center gap-0.5 text-xs', item.change < 0 ? 'text-destructive' : 'text-success-foreground')}>
                  {item.change < 0 ? <TrendingDown className="size-3" /> : <TrendingUp className="size-3" />}
                  {Math.abs(item.change)}%
                </p>
              )}
            </div>
          ))}
        </div>
        {card.note && <p className="mt-2 text-xs text-muted-foreground">{card.note}</p>}
      </div>
    )
  }

  if (card.kind === 'table') {
    return (
      <div className="overflow-hidden rounded-lg border border-border-subtle bg-card">
        <p className="px-3 pt-3 text-xs font-medium text-muted-foreground">{card.title}</p>
        <div className="overflow-x-auto p-3 pt-2">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-muted-foreground">
                {card.columns.map((c) => <th key={c} className="pb-1 pr-3 font-medium">{c}</th>)}
              </tr>
            </thead>
            <tbody>
              {card.rows.map((row, i) => (
                <tr key={i} className="border-t border-border-subtle">
                  {row.map((cell, j) => <td key={j} className="py-1 pr-3">{formatValue(cell)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {card.note && <p className="px-3 pb-3 text-xs text-muted-foreground">{card.note}</p>}
      </div>
    )
  }

  if (card.kind === 'checklist') {
    return (
      <div className="rounded-lg border border-border-subtle bg-card p-3">
        <p className="text-xs font-medium text-muted-foreground">{card.title}</p>
        <ul className="mt-2 space-y-1.5">
          {card.steps.map((step) => (
            <li key={step.id} className="flex items-center gap-2 text-xs">
              {step.status === 'done'
                ? <CheckCircle2 className="size-4 shrink-0 text-success-foreground" />
                : <Circle className="size-4 shrink-0 text-muted-foreground" />}
              {step.status !== 'done' && step.route_id && onRoute ? (
                <button type="button" onClick={() => onRoute(step.route_id!)} className="text-left text-link hover:underline">{step.label}</button>
              ) : (
                <span className={cn(step.status === 'done' && 'text-muted-foreground line-through')}>{step.label}</span>
              )}
            </li>
          ))}
        </ul>
      </div>
    )
  }

  return <ImageCard card={card} />
}

const ImageCard = ({ card }: { card: Extract<AssistantCard, { kind: 'image' }> }) => {
  const [loaded, setLoaded] = useState(false)
  const url = resolveMediaUrl(card.url)
  return (
    <figure className="overflow-hidden rounded-lg border border-border-subtle bg-card">
      <a href={url} target="_blank" rel="noopener noreferrer" className="relative block aspect-square bg-subdued">
        {!loaded && <span className="absolute inset-0 animate-pulse bg-subdued" />}
        <img
          src={url}
          alt={card.title}
          onLoad={() => setLoaded(true)}
          className={cn('size-full object-cover transition-opacity duration-300', loaded ? 'opacity-100' : 'opacity-0')}
        />
      </a>
      <figcaption className="space-y-2 px-3 py-2">
        <p className="truncate text-xs text-muted-foreground">{card.title}</p>
        <ImageActions name={card.title} path={card.url} mediaId={card.media_id} />
      </figcaption>
    </figure>
  )
}
